// Zoho WebToLead: main contact form (Contact, Home, Services, etc.) — from contact_us_form_v2.html
const ZOHO_MAIN_FORM = {
  xnQsjsdp: '88fb5c20bf58a4bc429fe31435fd98f94eff6ebe0ae8c3cfd9ac23b06a6ff197',
  xmIwtLD:
    '645be664ebae45bdeaf86eff0bd61e64a2b8e87e108ed0ced46f063590a2e167b278dfb1b1abb1b0daa4a9a0cd07a391',
}

// Zoho WebToLead: report download form (report_form_v2.html)
const ZOHO_REPORT_FORM = {
  xnQsjsdp: 'bf2273f923009f267c52ecbc6b15c979e0f8b74069d4ff65f55a72018828c16b',
  xmIwtLD:
    '59ee82584cf11f3ae2780b1c2def874204b1364b34c3fc967883a8cda5a65fb8c74640f0cf265982bebf24293de960da',
}

// The newsletter reuses the existing Lead webform contract. Its visitor-facing form
// remains email-only; mandatory CRM fields are supplied by submitNewsletterToZoho.
const ZOHO_NEWSLETTER_FORM = ZOHO_MAIN_FORM

const ZOHO_WEB_TO_LEAD_URL = 'https://crm.zoho.eu/crm/WebToLeadForm'

type ZohoWebToLeadOptions = {
  hiddenFields: Array<{ name: string; value: string | null }>
  formFields: Array<{ name: string; value: string }>
  onBeforeSuccess?: () => void | Promise<void>
}

async function submitZohoWebToLeadForm({
  hiddenFields,
  formFields,
  onBeforeSuccess,
}: ZohoWebToLeadOptions): Promise<void> {
  return await new Promise((resolve, reject) => {
    const form = document.createElement('form')
    form.method = 'POST'
    form.action = ZOHO_WEB_TO_LEAD_URL
    form.style.display = 'none'
    form.setAttribute('accept-charset', 'UTF-8')

    hiddenFields.forEach((field) => {
      const input = document.createElement('input')
      input.type = 'hidden'
      input.name = field.name
      input.value = field.value || ''
      form.appendChild(input)
    })

    formFields.forEach((field) => {
      const input = document.createElement('input')
      input.type = 'hidden'
      input.name = field.name
      input.value = field.value
      form.appendChild(input)
    })

    const iframeName = 'zoho-webtolead-iframe'
    const iframe = document.createElement('iframe')
    iframe.name = iframeName
    iframe.setAttribute('aria-hidden', 'true')
    iframe.style.cssText =
      'position:absolute;width:0;height:0;border:0;visibility:hidden;pointer-events:none;'

    document.body.appendChild(iframe)
    form.target = iframeName
    document.body.appendChild(form)

    try {
      form.submit()
      const finish = async () => {
        form.remove()
        try {
          await onBeforeSuccess?.()
          resolve()
        } catch (error) {
          reject(error)
        }
      }
      window.setTimeout(() => {
        void finish()
      }, 100)
      window.setTimeout(() => iframe.remove(), 1500)
    } catch (error) {
      form.remove()
      iframe.remove()
      reject(error instanceof Error ? error : new Error('Zoho form submission failed'))
    }
  })
}

type SubmitToZohoType = {
  company: string
  firstName: string
  lastName: string
  email?: string
  phone?: string
  description?: string
  /** Lead Source (contact_us_form_v2). Default "Contact webform". */
  leadSource?: string
  /** Called after form submit, before the promise resolves (e.g. close modal, open report). */
  onBeforeSuccess?: () => void | Promise<void>
}

export const submitToZoho = async ({
  company,
  firstName,
  lastName,
  email,
  phone,
  description,
  leadSource = 'Contact webform',
  onBeforeSuccess,
}: SubmitToZohoType): Promise<void> => {
  const hiddenFields = [
    { name: 'xnQsjsdp', value: ZOHO_MAIN_FORM.xnQsjsdp },
    { name: 'zc_gad', value: '' },
    { name: 'xmIwtLD', value: ZOHO_MAIN_FORM.xmIwtLD },
    { name: 'actionType', value: 'TGVhZHM=' },
    { name: 'returnURL', value: null },
  ]

  const formFields: Array<{ name: string; value: string }> = [
    { name: 'Company', value: company },
    { name: 'First Name', value: firstName },
    { name: 'Last Name', value: lastName },
  ]
  if (email) formFields.push({ name: 'Email', value: email })
  if (phone) formFields.push({ name: 'Phone', value: phone })
  if (description) formFields.push({ name: 'Description', value: description })
  if (leadSource) formFields.push({ name: 'Lead Source', value: leadSource })

  return await submitZohoWebToLeadForm({ hiddenFields, formFields, onBeforeSuccess })
}

export const submitNewsletterToZoho = async ({ email }: { email: string }): Promise<void> => {
  const hiddenFields = [
    { name: 'xnQsjsdp', value: ZOHO_NEWSLETTER_FORM.xnQsjsdp },
    { name: 'zc_gad', value: '' },
    { name: 'xmIwtLD', value: ZOHO_NEWSLETTER_FORM.xmIwtLD },
    { name: 'actionType', value: 'TGVhZHM=' },
    { name: 'returnURL', value: null },
  ]
  const formFields = [
    { name: 'Company', value: 'Newsletter Subscriber' },
    { name: 'First Name', value: 'Newsletter Subscriber' },
    { name: 'Last Name', value: email },
    { name: 'Email', value: email },
    { name: 'Lead Source', value: 'Website newsletter subscription' },
    { name: 'Cold/Warm Lead', value: 'Cold' },
  ]

  return await submitZohoWebToLeadForm({ hiddenFields, formFields })
}

type SubmitToZohoReportType = {
  company: string
  firstName: string
  lastName: string
  email?: string
  description?: string
  /** Lead Source (report_form_v2). Default "CA report webform". */
  leadSource?: string
  /** Called after form submit, before the promise resolves (e.g. close modal, open report). */
  onBeforeSuccess?: () => void | Promise<void>
}

export const submitToZohoReport = async ({
  company,
  firstName,
  lastName,
  email,
  description,
  leadSource = 'CA report webform',
  onBeforeSuccess,
}: SubmitToZohoReportType): Promise<void> => {
  const hiddenFields = [
    { name: 'xnQsjsdp', value: ZOHO_REPORT_FORM.xnQsjsdp },
    { name: 'zc_gad', value: '' },
    { name: 'xmIwtLD', value: ZOHO_REPORT_FORM.xmIwtLD },
    { name: 'actionType', value: 'TGVhZHM=' },
    { name: 'returnURL', value: null },
  ]

  const formFields: Array<{ name: string; value: string }> = [
    { name: 'First Name', value: firstName },
    { name: 'Last Name', value: lastName },
    { name: 'Email', value: email ?? '' },
    { name: 'Company', value: company },
  ]
  if (description) formFields.push({ name: 'Description', value: description })
  if (leadSource) formFields.push({ name: 'Lead Source', value: leadSource })

  return await submitZohoWebToLeadForm({ hiddenFields, formFields, onBeforeSuccess })
}
